// Import dependencies
import {  html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-builder-item-loop-add-filter-button' )
export class AutomatorBuilderItemLoopAddFilterButton extends LitElement {
	// Properties
	// The unique ID of this loop block
	@property( { type: Number, attribute: 'loop-id' } ) accessor loopID;

	// Information about what has to be iterated (e.g. users, posts, tokens)
	@property( { type: Object, attribute: false } ) accessor iterableExpression;

	// Create a reference to the dialog that doesn't exist yet
	$filterListDialog = createRef();

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="filters-actions">
				<uo-button
					color="secondary" 
					size="extra-small"
					@click=${ this.createFilterListDialog }
				>${ this.addFilterButtonLabel }</uo-button>
			</div>
		`;
	}

	/**
	 * Creates the dialog with the list of filters
	 */
	createFilterListDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ this.dialogHeading }

					size="large"
					force-manual-close
					open

					${ ref( this.$filterListDialog ) }
				>
					<uap-builder-item-loop-filter-directory
						loop-id=${ this.loopID }
						.iterableExpression=${ this.iterableExpression }
						
						@uap-builder-loop-filter-directory-selected=${ this.handleFilterSelected }
					></uap-builder-item-loop-filter-directory>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper,
			{ host: this }
		);
	}

	/**
	 * Handles the selection of the loop filter
	 *
	 * @param {Event} event The selection event
	 */
	handleFilterSelected( event ) {
		// Close the filter list dialog
		this.$filterListDialog.value.close();

		// Open the dialog to set the loop filter fields
		this.createEditFieldsDialog(
			event.detail.integrationCode,
			event.detail.loopFilterCode
		);
	}

	/**
	 * Creates the dialog that lets the user edit the fields of the loop filter
	 *
	 * @param {String} integrationCode The integration code
	 * @param {String} loopFilterCode The loop filter code
	 */
	createEditFieldsDialog( integrationCode, loopFilterCode ) {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uap-builder-item-loop-filter-edit-dialog
					loop-id=${ this.loopID }
					loop-filter-code=${ loopFilterCode }
					integration-code=${ integrationCode }
				></uap-builder-item-loop-filter-edit-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * Returns the heading of the dialog that lets the user add a filter
	 *
	 * @return {String|undefined} The heading of the dialog
	 */
	get dialogHeading() {
		if ( this.iterableExpression.type === 'users' ) {
			return escapeHTML( __( 'Filter users', 'uncanny-automator' ) );
		}

		if ( this.iterableExpression.type === 'posts' ) {
			return escapeHTML( __( 'Filter posts', 'uncanny-automator' ) );
		}

		if ( this.iterableExpression.type === 'token' ) {
			return escapeHTML( __( 'Filter data', 'uncanny-automator' ) );
		}
	}

	/**
	 * Returns the label of the button that lets the user add a filter
	 *
	 * @return {String|undefined} The label of the button
	 */
	get addFilterButtonLabel() {
		if ( this.iterableExpression.type === 'users' ) {
			return escapeHTML( __( 'Filter users', 'uncanny-automator' ) );
		}

		if ( this.iterableExpression.type === 'posts' ) {
			return escapeHTML( __( 'Filter posts', 'uncanny-automator' ) );
		}

		if ( this.iterableExpression.type === 'token' ) {
			return escapeHTML( __( 'Filter data', 'uncanny-automator' ) );
		}
	}
}